import { word } from "@modules/core/utils/i18n"
import { FeatureKeys } from "@modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@modules/featureToggle/hooks/useFeatureToggle"
import { VIDEO_MAX_SIZE } from "@modules/ui/components/fileUpload/fileUploadUtils"
import { HUText } from "@modules/ui/components/huText"
import { ThemeMode } from "@modules/ui/styles/theme"
import { FileInputIcon } from "lucide-react"
import { useState } from "react"
import { DefaultTheme, useTheme } from "styled-components"

export function MediaUploadDragZone({
  openFileSelector,
  disabled,
}: {
  openFileSelector: () => void
  disabled?: boolean
}) {
  const [isHovered, setIsHovered] = useState(false)

  const toggleHovered = (value: boolean) => {
    if (disabled) return
    setIsHovered(value)
  }

  const theme = useTheme()
  const componentColors = getComponentColors(theme)
  const canUseMultipleMedias = useFeatureToggle(FeatureKeys.MultipleMedias)

  return (
    <div
      className={`flex align-items-center flex-column justify-content-center h-full gap-2 transition-duration-200 transition-all ${disabled ? "" : "cursor-pointer"} ${isHovered ? componentColors.backgroundHover : componentColors.background}`}
      style={{ backgroundColor: isHovered ? componentColors.backgroundHover : componentColors.background }}
      onMouseEnter={() => toggleHovered(true)}
      onMouseLeave={() => toggleHovered(false)}
      onClick={openFileSelector}
    >
      <div
        className="flex justify-content-center align-items-center rounded-full transition-duration-200 transition-all"
        style={{
          height: 80,
          width: 80,
          backgroundColor: isHovered ? componentColors.iconBackgroundHover : componentColors.iconBackground,
        }}
      >
        <FileInputIcon
          color={isHovered ? componentColors.iconHover : componentColors.icon}
          size={38}
          className="transition-duration-200 transition-all"
        />
      </div>
      <HUText
        fontStyle="BL"
        color={isHovered ? componentColors.titleHover : componentColors.title}
        className="text-center transition-duration-200 transition-all"
      >
        {word("sessionForm.blocks.add_media.drag_and_drop")}
      </HUText>
      <HUText
        fontStyle="BS"
        color={isHovered ? componentColors.subtitleHover : componentColors.subtitle}
        className="text-center transition-duration-200 transition-all px-3"
        style={{ whiteSpace: "pre-line" }}
      >
        {canUseMultipleMedias
          ? word("sessionForm.blocks.add_media.allowed_format", { max_size: VIDEO_MAX_SIZE / 1_000_000 })
          : word("sessionForm.blocks.add_media.upload_video.description", { maxSize: VIDEO_MAX_SIZE / 1_000_000 })}
      </HUText>
    </div>
  )
}

const getComponentColors = (theme: DefaultTheme) => ({
  background: theme.neutralColor0,
  backgroundHover: theme.mode === ThemeMode.ATHLETE ? theme.primaryColor100 : theme.primaryColor800,
  title: theme.neutralColor600,
  titleHover: theme.mode === ThemeMode.ATHLETE ? theme.secondaryColor : theme.primaryColor300,
  subtitle: theme.neutralColor500,
  subtitleHover: theme.mode === ThemeMode.ATHLETE ? theme.secondaryColor400 : theme.primaryColor200,
  icon: theme.neutralColor600,
  iconHover: theme.mode === ThemeMode.ATHLETE ? theme.secondaryColor600 : theme.primaryColor700,
  iconBackground: theme.neutralColor100,
  iconBackgroundHover: theme.mode === ThemeMode.ATHLETE ? theme.primaryColor : theme.primaryColor400,
})
